/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview PlayerMicToggles — Per-participant mic toggle list for player_level mode
 * @version 1.0.0
 * @since PRD_PlayerLevelMicControls_v1.md Step 11
 *
 * WHO: Voice pipeline team — Step 11 (PRD_PlayerLevelMicControls_v1.md)
 * WHAT: Renders a list of session participants with per-player mic Switch toggles.
 *   - Own row: Switch is interactive; toggling calls onToggle(userId, enabled)
 *   - Other rows: Switch is disabled (read-only — display awareness of others' state)
 *   - "You" badge (blue) highlights the current user's row
 *   - Character name badge (purple) shown when participant has an active character
 *   - Role badge (yellow for GM, gray for spectator, none for player)
 * WHY: multi_speaker_mode='player_level' requires individual audio gating per participant.
 *   Each player controls their own mic; others' states are visible but not modifiable.
 *   Audio gating is enforced client-side in AudioProcessor.setMicEnabled() — this component
 *   is the UI trigger for that call.
 *
 * Data sources:
 * - participants: Participant[] from CampaignContext (via VoicePanel parent)
 * - micStates: Record<string, boolean> from VoicePanel (localStorage-persisted per sessionId)
 * - currentUserId: participantId prop from VoicePanel (identifies own row)
 *
 * Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
 */

import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import type { Participant } from '../../services/api/CampaignService';

// =============================================================================
// TYPES
// =============================================================================

export interface PlayerMicTogglesProps {
  /** Participants in the active session — from CampaignContext.participants */
  participants: Participant[];
  /**
   * Mic enabled state keyed by user_id. Missing keys default to true (enabled).
   * Managed by parent (VoicePanel) and persisted to localStorage.
   */
  micStates: Record<string, boolean>;
  /**
   * Called when a participant's mic toggle changes.
   * Parent (VoicePanel) is responsible for calling audioProcessor.setMicEnabled()
   * when userId === currentUserId, and for updating micStates.
   */
  onToggle: (userId: string, enabled: boolean) => void;
  /** The current user's participant ID — used to identify own row and enable its Switch */
  currentUserId?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Returns role badge color scheme for Chakra UI Badge.
 * GM is highlighted (yellow); spectator is muted (gray); player has no badge.
 * Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
 */
function getRoleBadge(role: Participant['role']): { label: string; colorScheme: string } | null {
  switch (role) {
    case 'gm':
      return { label: 'GM', colorScheme: 'yellow' };
    case 'spectator':
      return { label: 'Spectator', colorScheme: 'gray' };
    case 'player':
    default:
      return null; // No badge for regular players
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * PlayerMicToggles — Renders per-participant mic controls for player_level mode.
 *
 * Only rendered by VoicePanel when multi_speaker_mode === 'player_level'.
 * State (micStates) is owned and persisted by VoicePanel; this component is
 * purely presentational + event-emitting.
 *
 * Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
 */
export const PlayerMicToggles: React.FC<PlayerMicTogglesProps> = ({
  participants,
  micStates,
  onToggle,
  currentUserId,
}) => {
  return (
    <Box>
      {/* Section heading */}
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="whiteAlpha.600"
        textTransform="uppercase"
        letterSpacing="wider"
        mb={2}
      >
        Per-Player Mic Controls
      </Text>

      {participants.length === 0 ? (
        // Empty state — no participants yet in session
        <Text fontSize="sm" color="whiteAlpha.400" fontStyle="italic">
          No participants in session.
        </Text>
      ) : (
        <VStack spacing={1} align="stretch">
          {participants.map((p) => {
            const isOwn = p.user_id === currentUserId;
            // Default to enabled (true) when not explicitly set in micStates
            const isEnabled = micStates[p.user_id] !== false;
            const roleBadge = getRoleBadge(p.role);

            return (
              <FormControl
                key={p.user_id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1}
                borderRadius="md"
                bg={isOwn ? 'whiteAlpha.100' : 'transparent'}
                _hover={isOwn ? { bg: 'whiteAlpha.150' } : undefined}
              >
                {/* Left: name + badges */}
                <HStack spacing={1} flex={1} minW={0} pr={2}>
                  <FormLabel
                    htmlFor={`mic-toggle-${p.user_id}`}
                    mb={0}
                    cursor={isOwn ? 'pointer' : 'default'}
                    flex={1}
                    minW={0}
                  >
                    <HStack spacing={1} flexWrap="wrap">
                      {/* Screen name */}
                      <Text
                        fontSize="sm"
                        color={isOwn ? 'white' : 'whiteAlpha.700'}
                        fontWeight={isOwn ? 'semibold' : 'normal'}
                        isTruncated
                        maxW="120px"
                      >
                        {p.screen_name || p.user_id}
                      </Text>

                      {/* "You" badge — current user only */}
                      {isOwn && (
                        <Badge colorScheme="blue" fontSize="2xs" variant="solid">
                          You
                        </Badge>
                      )}

                      {/* Character name badge */}
                      {p.character_name && (
                        <Badge colorScheme="purple" fontSize="2xs" variant="subtle">
                          {p.character_name}
                        </Badge>
                      )}

                      {/* Role badge (GM / Spectator only) */}
                      {roleBadge && (
                        <Badge
                          colorScheme={roleBadge.colorScheme}
                          fontSize="2xs"
                          variant="outline"
                        >
                          {roleBadge.label}
                        </Badge>
                      )}
                    </HStack>
                  </FormLabel>
                </HStack>

                {/* Right: mic Switch */}
                <Switch
                  id={`mic-toggle-${p.user_id}`}
                  isChecked={isEnabled}
                  isDisabled={!isOwn}
                  onChange={(e) => onToggle(p.user_id, e.target.checked)}
                  colorScheme="green"
                  size="sm"
                  aria-label={
                    isOwn
                      ? `Toggle your mic ${isEnabled ? 'off' : 'on'}`
                      : `${p.screen_name || p.user_id} mic is ${isEnabled ? 'on' : 'off'}`
                  }
                />
              </FormControl>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default PlayerMicToggles;
